import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { FAMILIES, BATTERY_CONDITIONS } from "@/lib/format";
import { Camera, Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  family: "MacBook Pro", size: "", year: "", chip: "", cpu_note: "", memory: "", storage: "",
  display: "", macos: "", battery_cycles: "", battery_capacity: "", battery_condition: "Normal",
  colour: "", serial: "", purchase_cost: "", price: "", notes: "",
};

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AddMac() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setPhoto(dataUrl);
    scan(dataUrl);
  };

  const scan = async (dataUrl) => {
    setScanning(true);
    setScanned(false);
    try {
      const { data } = await api.post("/units/extract", { image_base64: dataUrl });
      setForm((f) => ({
        ...f,
        family: data.family || f.family,
        size: data.size || f.size,
        year: data.year ?? f.year,
        chip: data.chip || f.chip,
        cpu_note: data.cpu_note || "",
        memory: data.memory || f.memory,
        storage: data.storage || f.storage,
        display: data.display || f.display,
        macos: data.macos || f.macos,
        battery_cycles: data.battery_cycles ?? f.battery_cycles,
        battery_capacity: data.battery_capacity ?? f.battery_capacity,
        battery_condition: data.battery_condition || "Normal",
        colour: data.colour || f.colour,
        serial: data.serial || f.serial,
      }));
      setScanned(true);
      toast.success("Specs read from photo — check & add price");
    } catch (e) {
      toast.error(apiError(e, "Couldn't read the photo. Enter details manually."));
    } finally {
      setScanning(false);
    }
  };

  const save = async () => {
    if (!form.chip || !form.size) {
      toast.error("Please fill at least size and chip");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        battery_cycles: form.battery_cycles === "" ? null : Number(form.battery_cycles),
        battery_capacity: form.battery_capacity === "" ? null : Number(form.battery_capacity),
        purchase_cost: form.purchase_cost === "" ? null : Number(form.purchase_cost),
        price: form.price === "" ? null : Number(form.price),
        about_photo: photo,
      };
      const { data } = await api.post("/units", payload);
      toast.success(`Saved ${data.stock_code}`);
      navigate(`/unit/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-[#98989d]">{label}</label>
      <input
        data-testid={`field-${key}`}
        value={form[key] ?? ""}
        onChange={(e) => set(key, e.target.value)}
        className="h-11 w-full rounded-xl border border-white/10 bg-black px-3 text-sm outline-none focus:border-[#2997ff]"
        {...props}
      />
    </div>
  );

  return (
    <div className="mf-fade-in">
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight">Add a Mac</h1>
      <p className="mb-5 text-sm text-[#98989d]">Snap “About This Mac” — AI fills the rest</p>

      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="photo-input" />

      {!photo ? (
        <button
          data-testid="take-photo-button"
          onClick={() => fileRef.current?.click()}
          className="flex w-full flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-white/15 bg-[#121214] py-14 transition-colors active:scale-[0.99]"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#2997ff]/15 text-[#2997ff]">
            <Camera size={28} />
          </span>
          <span className="font-semibold">Take / upload photo</span>
          <span className="text-xs text-[#98989d]">About This Mac + System Report → Power</span>
        </button>
      ) : (
        <div className="relative overflow-hidden rounded-3xl border border-white/10">
          <img src={photo} alt="scan" className="max-h-56 w-full object-cover opacity-90" />
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-3 right-3 rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold backdrop-blur"
          >
            Retake
          </button>
          {scanning && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 text-sm font-medium backdrop-blur">
              <Loader2 className="animate-spin text-[#2997ff]" size={18} /> Reading specs…
            </div>
          )}
        </div>
      )}

      {scanned && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-[#30d158]/30 bg-[#30d158]/10 px-3 py-2 text-sm text-[#30d158]">
          <Sparkles size={15} /> AI prefilled the fields below. Review and add a price.
        </div>
      )}

      <div className="mt-4 space-y-3 rounded-3xl border border-white/10 bg-[#121214] p-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-[#98989d]">Family</label>
            <select data-testid="field-family" value={form.family} onChange={(e) => set("family", e.target.value)} className="h-11 w-full rounded-xl border border-white/10 bg-black px-3 text-sm outline-none focus:border-[#2997ff]">
              {FAMILIES.map((f) => <option key={f}>{f}</option>)}
            </select>
          </div>
          {field("Size", "size", { placeholder: '14"' })}
          {field("Year", "year", { type: "number", placeholder: "2023" })}
          {field("Chip", "chip", { placeholder: "M3 Pro" })}
        </div>
        {form.chip?.toLowerCase().includes("intel") && field("CPU note (Intel)", "cpu_note", { placeholder: "2.6 GHz 6-core · Intel UHD 630" })}
        <div className="grid grid-cols-2 gap-3">
          {field("Memory", "memory", { placeholder: "16GB" })}
          {field("Storage", "storage", { placeholder: "512GB" })}
        </div>
        {field("Display", "display", { placeholder: '14" Liquid Retina XDR · 3024 × 1964' })}
        {field("macOS", "macos", { placeholder: "macOS Sonoma 14.8" })}
        <div className="grid grid-cols-3 gap-3">
          {field("Cycles", "battery_cycles", { type: "number" })}
          {field("Max cap %", "battery_capacity", { type: "number" })}
          <div>
            <label className="mb-1 block text-xs font-medium text-[#98989d]">Battery</label>
            <select data-testid="field-battery_condition" value={form.battery_condition} onChange={(e) => set("battery_condition", e.target.value)} className="h-11 w-full rounded-xl border border-white/10 bg-black px-2 text-xs outline-none focus:border-[#2997ff]">
              {BATTERY_CONDITIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {field("Colour", "colour", { placeholder: "Space Grey" })}
          {field("Serial (private)", "serial")}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {field("Purchase cost ₹ (private)", "purchase_cost", { type: "number" })}
          {field("Selling price ₹", "price", { type: "number", placeholder: "125000" })}
        </div>
      </div>

      <button
        data-testid="save-unit-button"
        onClick={save}
        disabled={saving}
        className="mt-4 flex h-13 w-full items-center justify-center gap-2 rounded-2xl bg-[#2997ff] py-3.5 font-semibold transition-transform active:scale-[0.98] disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} Save to inventory
      </button>
    </div>
  );
}
